import {useApiQuery} from '@wallaby-ui/core';

/**
 * Mirrors the BE `StructuredMedicationRowDto` from PDW-48. Kept hand-written here
 * because the BE patch is not yet merged and therefore not present in the
 * generated OpenAPI schemas. Once the BE merges and schemas regenerate, swap
 * these for the generated types.
 */
// Matches the enums already in the generated schemas
// (PatientMedicationUsageTypeEnum, PatientMedicationRouteOfAdministrationTypeEnum).
export type PatientMedicationUsageType = 'ACUTE' | 'CHRONIC' | 'UNKNOWN';
export type PatientMedicationRouteOfAdministration =
  | 'INTRAVENOUS'
  | 'SUBCUTANEOUS'
  | 'INTRAMUSCULAR'
  | 'PERORAL'
  | 'INJECTION'
  | 'INFUSION'
  | 'OTHER'
  | 'UNKNOWN';

export interface StructuredMedicationRowDto {
  /** PDW-48 update: one row per patient_medication_source (was per patient_medication). */
  sourceId: number;
  prescribedAt: string | null;
  administeredAt: string | null;
  administeredUntil: string | null;
  /** Renamed from `originalMedicationName`. Comes from the source. */
  medicationName: string | null;
  usageType: PatientMedicationUsageType;
  medicationStrength: string | null;
  dosage: string | null;
  routeOfAdministration: PatientMedicationRouteOfAdministration;
  comment: string | null;
  atcGroupLabels: string[];
  activeIngredientLabels: string[];
  /** Aggregated across all patient_medications for the source (source.isDiscontinued
      OR any patientMedication.isDiscontinued). */
  isDiscontinued: boolean;
}

export interface StructuredMedicationDocumentDto {
  documentVersionProperties: {
    documentType: string;
    documentName: string;
    documentOrganizationShortLabel: string;
    documentOrganizationFullLabel: string;
    documentTimestamp: string | null;
    versionTimestamp: string;
    effectiveAtTimestamp: string;
    documentSrcId: string | null;
    documentId: number;
    versionId: number;
  };
  medications: StructuredMedicationRowDto[];
}

const STRUCTURED_MEDICATIONS_URL = '/wallabyCoreApi/api/patient-medications/document-version';

export interface UseLoadStructuredMedicationsParams {
  documentVersionId?: string | number;
  enabled?: boolean;
}

export interface UseLoadStructuredMedicationsReturnValue {
  data: StructuredMedicationDocumentDto | undefined;
  isLoading: boolean;
  isError: boolean;
}

export function useLoadStructuredMedications(
  params: UseLoadStructuredMedicationsParams
): UseLoadStructuredMedicationsReturnValue {
  const {documentVersionId, enabled = true} = params;
  const apiUrl = `${STRUCTURED_MEDICATIONS_URL}/${documentVersionId ?? ''}`;
  const {data, isLoading, isError} = useApiQuery<StructuredMedicationDocumentDto>([apiUrl], null, {
    axiosConfig: {url: apiUrl},
    reactQueryOptions: {
      enabled: enabled && Boolean(documentVersionId),
    },
  });
  return {data, isLoading, isError};
}
